import {IBondModel, AtomContainerQuery, IAtomModel} from "@alchemiesolns/atomcontainer"
import { ElementHelper } from "@alchemiesolns/elements";
import { v3 } from "@alchemiesolns/maths";

/**
 * Whether a substituent label should read right-to-left on the page (e.g. H₃C vs CH₃,
 * OH₂ vs H₂O on oxygen with collapsed hydrogens).
 *
 * For **periodic-table element** nodes (single `symbol` like O, C, Br), when there is
 * at least one bond to a non-hydrogen neighbor we use only those **skeleton** bonds:
 * the average Δx from `root` to those neighbors decides the flip. Hydrogen positions
 * (including hidden explicit H used for implicit display) never affect that average.
 *
 * For **non-element** labels (e.g. SnBu₃, TBDMS), the legacy average incoming bond
 * direction is preserved so reversible functional-group strings behave as before.
 */
export const isLeftSidePaperLabel = (root: IAtomModel, bonds: IBondModel[]) => {
    if (!root.pos || bonds.length === 0) {
        return false;
    }

    const useSkeletonHeuristic = ElementHelper.findElement(root.symbol) !== null;

    if (useSkeletonHeuristic) {
        const skeletonBonds = bonds.filter((b) => {
            const other = b.a1 === root ? b.a2 : b.a1;
            return other.symbol !== "H";
        });

        if (skeletonBonds.length > 0) {
            let sumDeltaX = 0;
            let n = 0;
            for (const b of skeletonBonds) {
                const other = b.a1 === root ? b.a2 : b.a1;
                if (!other.pos) continue;
                sumDeltaX += other.pos.x - root.pos.x;
                n++;
            }
            if (n > 0) {
                return sumDeltaX / n < 0;
            }
        }
    }

    const averageBondDirection = bonds.reduce((acc, b) => {
        let dir = AtomContainerQuery.getBondDirection(b);
        if (b.a1 === root) {
            dir = v3.negate(dir);
        }
        acc = v3.addVectors(acc, dir);
        return acc;
    }, [0, 0, 0]);
    return averageBondDirection[0] < 0;
}